import { DataTypes, DataVariants } from "../../../data/DataManager";
import { GridFilterTypes } from "../../../grid/AgGridManager";
import { InputTypes } from "../../../form/FormManager";
import { EMPTY_VALUE, FieldSchema } from "../../../Schema";
import { Building, getAndProcessBuildings, getBuilding } from "../external-sources/Buildings";
import { Site } from "../external-sources/Sites";
import { KeyValueConstruct } from "..";
import { Modes } from "./mode";
import { LinkedInstance } from "../external-sources/LinkedInstances";
import { User } from "../external-sources/Users";
import { EntryTypes } from "./entryType";
import { SchemaContext } from "../../common";

export const createBuildingLocation = (context: SchemaContext): FieldSchema => ({
  name: "buildingLocation",
  grid: {
    displayed: true,
    order: 33,
    filter: GridFilterTypes.TEXT,
  },
  graphql: { selection: "buildingLocation { key value }" },
  dataTransform: {
    fromGraphql: {
      getter: async ({ siteName, buildingLocation }) => {
        const siteId = context.sites.find((s) => s.siteName === siteName)?.siteId;
        const buildingId = Number((buildingLocation as KeyValueConstruct)?.key);
        if (siteId && buildingId) {
          const buildingData = await getBuilding(context.client, buildingId);
          if (buildingData && buildingData.siteId === siteId) {
            return { id: buildingData.id, buildingNumber: buildingData.buildingNumber, siteId: buildingData.siteId };
          }
        }
        return EMPTY_VALUE;
      },
      fields: ["siteName"],
    },
    toGraphql: {
      getter: ({ buildingLocation }) => {
        if (buildingLocation) {
          return {
            key: (buildingLocation as Building)?.id,
            value: (buildingLocation as Building)?.buildingNumber,
          };
        }
        return EMPTY_VALUE;
      },
    },
    // The same key/value pair as GraphQL, but with the id as text: that is how every
    // stored record has it, and DynamoDB keeps the difference between "17272" and 17272.
    toDb: {
      getter: ({ buildingLocation }) => {
        if (buildingLocation) {
          return {
            key: String((buildingLocation as Building)?.id),
            value: (buildingLocation as Building)?.buildingNumber,
          };
        }
        return EMPTY_VALUE;
      },
    },
  },
  data: {
    type: DataTypes.OBJECT,
    variant: DataVariants.ENUM,
    options: {
      getter: async ({ siteName }) => {
        // Guarded on the key rather than on `siteName` being truthy: an enum parser keeps
        // the raw cell text when it matches no option, so this may be the string a file
        // carried rather than a resolved Site. A missing key is a query with an undefined
        // binding, which DynamoDB rejects outright where GraphQL quietly accepted it.
        const siteId = (siteName as Site)?.siteId;
        if (!siteId) {
          return [];
        }
        return (await getAndProcessBuildings(context.client, siteId))
          .map((b: Building) => ({ value: b, label: b.buildingNumber }));
      },
      fields: ["siteName"],
    },
  },
  display: {
    displayed: true,
    translator: (value) => [(value as Building).buildingNumber as string],
  },
  validation: { required: true },
  form: {
    label: "Building",
    type: InputTypes.SELECT,
    disabled: {
      getter: ({ entryType, siteName, linkedInstance, mode, currentUser }) => {
        return !siteName ||
        (
          [EntryTypes.STANDALONE_EQUIPMENT, EntryTypes.CLUSTER_SUBEQUIPMENT].includes(entryType as EntryTypes) &&
          [Modes.EDIT, Modes.BULK_EDIT].includes(mode as Modes) &&
          ["P1S Uni-Directional"].includes((linkedInstance as LinkedInstance)?.linkedInstance)
        ) && !(currentUser as User)?.isAdmin;
      },
      fields: ["entryType", "siteName", "linkedInstance", "mode", "currentUser"],
    },
  },
});
